import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import {
  fetchAccountEligibility,
  saveAccountEligibility,
} from "./accountEligibilityApi";
import {
  EARLIEST_DATE_OF_BIRTH,
  REGION_NAME_MAX_LENGTH,
  buildEligibilityPayload,
  initialValues,
  lockedLocation,
  regionLabel,
  regionMode,
  shouldPrompt,
  subdivisionsFor,
  todayIso,
  validateEligibilityForm,
} from "./accountEligibility";
import "./AccountCompletionGate.css";

/**
 * Post-login account completion prompt (date of birth, country, region).
 *
 * Mounted next to the Legal ConsentGate in UserLayout and follows its pattern
 * (react-bootstrap Modal, static backdrop, checked once per signed-in session),
 * but it never blocks the platform: "Not now" hides it for the rest of the
 * session. Age/location-restricted features are enforced on the server. It
 * waits while the consent modal is open so the two never stack.
 */

// Session-scoped memo so the prompt does not re-fetch / re-open on every route.
const clearedForUser = {};

const CONSENT_MODAL_SELECTOR = ".legal-consent-gate-modal";

const CONSENT_POLL_MS = 1000;

class AccountCompletionGate extends Component {
  state = {
    show: false,
    status: null,
    values: initialValues(null),
    errors: {},
    serverError: null,
    submitting: false,
  };

  componentDidMount() {
    this._mounted = true;
    this.checkStatus();
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._consentTimer) {
      clearInterval(this._consentTimer);
    }
  }

  currentUserId() {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("accessToken");
    if (!userId || !token) {
      return null;
    }
    return userId;
  }

  checkStatus() {
    const userId = this.currentUserId();
    if (!userId || clearedForUser[userId]) {
      return;
    }

    fetchAccountEligibility()
      .then((status) => {
        if (!this._mounted) {
          return;
        }
        if (shouldPrompt(status)) {
          this.setState({ status, values: initialValues(status) });
          this.openWhenConsentClosed();
        } else if (status) {
          clearedForUser[userId] = true;
        }
      })
      .catch(() => {});
  }

  consentModalOpen() {
    return (
      typeof document !== "undefined" &&
      !!document.querySelector(CONSENT_MODAL_SELECTOR)
    );
  }

  openWhenConsentClosed() {
    if (!this.consentModalOpen()) {
      this.setState({ show: true });
      return;
    }
    this._consentTimer = setInterval(() => {
      if (!this._mounted) {
        clearInterval(this._consentTimer);
        return;
      }
      if (!this.consentModalOpen()) {
        clearInterval(this._consentTimer);
        this._consentTimer = null;
        this.setState({ show: true });
      }
    }, CONSENT_POLL_MS);
  }

  dobLocked() {
    return !!(this.state.status && this.state.status.date_of_birth_locked);
  }

  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState((prev) => {
      const values = { ...prev.values, [name]: value };
      const errors = { ...prev.errors };
      delete errors[name];
      if (name === "country_code") {
        values.region_code = "";
        delete errors.region_code;
        delete errors.region_name;
        delete errors.region;
      }
      return { values, errors, serverError: null };
    });
  };

  handleDismiss = () => {
    const userId = this.currentUserId();
    if (userId) {
      clearedForUser[userId] = true;
    }
    this.setState({ show: false });
  };

  handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (this.state.submitting) {
      return;
    }
    const context = {
      dobLocked: this.dobLocked(),
      options: this.state.status && this.state.status.options,
      today: todayIso(),
    };
    const errors = validateEligibilityForm(this.state.values, context);
    if (Object.keys(errors).length > 0) {
      this.setState({ errors, serverError: null });
      return;
    }

    this.setState({ submitting: true, errors: {}, serverError: null });
    saveAccountEligibility(buildEligibilityPayload(this.state.values, context))
      .then((result) => {
        if (!this._mounted) {
          return;
        }
        if (result.success && result.data && result.data.complete) {
          const userId = this.currentUserId();
          if (userId) {
            clearedForUser[userId] = true;
          }
          this.setState({ show: false, submitting: false, status: result.data });
          return;
        }
        this.setState({
          submitting: false,
          errors: (result && result.errors) || {},
          serverError:
            (result && result.message) ||
            "We could not save your details. Please check the form and try again.",
        });
      })
      .catch(() => {
        if (this._mounted) {
          this.setState({
            submitting: false,
            serverError: "We could not save your details. Please try again.",
          });
        }
      });
  };

  renderError(field) {
    const message = this.state.errors[field];
    if (!message) {
      return null;
    }
    return (
      <div className="account-completion-error" id={"account-completion-" + field + "-error"} role="alert">
        {message}
      </div>
    );
  }

  renderRegion(options) {
    const { values, errors } = this.state;
    const country = values.country_code;
    const mode = regionMode(country, options);
    if (mode === "none") {
      return null;
    }
    const label = regionLabel(country);

    if (mode === "select") {
      const list = subdivisionsFor(country, options);
      return (
        <div className="form-group">
          <label htmlFor="account-completion-region-code">
            {label} <span className="account-completion-required" aria-hidden="true">*</span>
          </label>
          <select
            id="account-completion-region-code"
            name="region_code"
            className={"form-control" + (errors.region_code ? " is-invalid" : "")}
            value={values.region_code}
            onChange={this.handleChange}
            aria-required="true"
            aria-invalid={errors.region_code ? "true" : "false"}
            aria-describedby={errors.region_code ? "account-completion-region_code-error" : undefined}
          >
            <option value="">Select…</option>
            {list.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
          {this.renderError("region_code")}
        </div>
      );
    }

    return (
      <div className="form-group">
        <label htmlFor="account-completion-region-name">
          {label} <span className="account-completion-required" aria-hidden="true">*</span>
        </label>
        <input
          type="text"
          id="account-completion-region-name"
          name="region_name"
          className={"form-control" + (errors.region_name ? " is-invalid" : "")}
          value={values.region_name}
          onChange={this.handleChange}
          maxLength={REGION_NAME_MAX_LENGTH}
          autoComplete="address-level1"
          aria-required="true"
          aria-invalid={errors.region_name ? "true" : "false"}
          aria-describedby={errors.region_name ? "account-completion-region_name-error" : undefined}
        />
        {this.renderError("region_name")}
      </div>
    );
  }

  render() {
    const { show, status, values, errors, serverError, submitting } = this.state;

    if (!show || !status) {
      return null;
    }

    const options = status.options || {};
    const countries = Array.isArray(options.countries) ? options.countries : [];
    const dobLocked = this.dobLocked();
    const location = lockedLocation(status);

    return (
      <Modal
        show={show}
        backdrop="static"
        keyboard={false}
        onHide={this.handleDismiss}
        centered
        dialogClassName="account-completion-gate-modal"
      >
        <form onSubmit={this.handleSubmit} noValidate>
          <Modal.Header>
            <Modal.Title>Complete your account</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Please add your date of birth and where you live. Some features are
              only available to eligible account holders in certain locations.
            </p>

            <div className="form-group">
              <label htmlFor="account-completion-dob">
                Date of birth <span className="account-completion-required" aria-hidden="true">*</span>
              </label>
              {dobLocked ? (
                <p className="account-completion-locked" id="account-completion-dob">
                  On file
                </p>
              ) : (
                <input
                  type="date"
                  id="account-completion-dob"
                  name="date_of_birth"
                  className={"form-control" + (errors.date_of_birth ? " is-invalid" : "")}
                  value={values.date_of_birth}
                  onChange={this.handleChange}
                  min={EARLIEST_DATE_OF_BIRTH}
                  max={todayIso()}
                  autoComplete="bday"
                  aria-required="true"
                  aria-invalid={errors.date_of_birth ? "true" : "false"}
                  aria-describedby={errors.date_of_birth ? "account-completion-date_of_birth-error" : undefined}
                />
              )}
              {this.renderError("date_of_birth")}
            </div>

            {location ? (
              <>
                <div className="form-group">
                  <label htmlFor="account-completion-country">Country</label>
                  <p className="account-completion-locked" id="account-completion-country">
                    {location.country}
                  </p>
                  {this.renderError("country_code")}
                </div>
                <div className="form-group">
                  <label htmlFor="account-completion-region">{location.regionLabel}</label>
                  <p className="account-completion-locked" id="account-completion-region">
                    {location.region}
                  </p>
                </div>
              </>
            ) : (
            <>
            <div className="form-group">
              <label htmlFor="account-completion-country">
                Country <span className="account-completion-required" aria-hidden="true">*</span>
              </label>
              <select
                id="account-completion-country"
                name="country_code"
                className={"form-control" + (errors.country_code ? " is-invalid" : "")}
                value={values.country_code}
                onChange={this.handleChange}
                autoComplete="country"
                aria-required="true"
                aria-invalid={errors.country_code ? "true" : "false"}
                aria-describedby={errors.country_code ? "account-completion-country_code-error" : undefined}
              >
                <option value="">Select…</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
              {this.renderError("country_code")}
            </div>

            {this.renderRegion(options)}
            </>
            )}

            <p className="account-completion-note">
              <span aria-hidden="true">*</span> Required
            </p>

            {serverError ? (
              <p className="account-completion-server-error" role="alert">
                {serverError}
              </p>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-link account-completion-later"
              onClick={this.handleDismiss}
              disabled={submitting}
            >
              Not now
            </button>
            <button type="submit" className="btn btn-danger" disabled={submitting}>
              {submitting ? "Saving..." : "Save & Continue"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

export default AccountCompletionGate;
